import React, { Fragment, useEffect } from 'react';
import { t, Trans } from '@lingui/macro';
import { connect } from 'dva';
import { Redirect, routerRedux } from 'dva/router';
import { Heading, Paragraph } from '@mos-cat/typography';
import cn from 'classnames';
import { ReactComponent as Print } from './img/Print.svg';
import { ReactComponent as Logo } from './img/full.svg';

import SuccessError from '@mos-front-components/public/es/components/SuccessError';
import Link from '../../components/shared/Link/Link';
import './ResultPage.pcss';
import Success from './components/Success';
import { i18n } from '../../helpers/i18n';
import { landingPageError, sendStats } from '../../helpers/stats';
import '../../print.css';

export type LocationState =
  | { status: null; statusName: never }
  | { status: 'success'; statusName?: never }
  | { status: 'error'; statusName: string };

export interface Props {
  serviceNumber?: string;
  location: {
    state: LocationState;
  };

  resetLocationState(): void;
}

const statusTexts = {
  success: {
    title: <Trans>Заявление успешно отправлено</Trans>,
    text: [<Trans>Указанные сведения будут проверены на корректность</Trans>],
  },
  error: {
    serviceNotAvailable: {
      title: <Trans>Услуга недоступна</Trans>,
      text: [
        <Trans>
          При выполнении запроса произошла ошибка. Пожалуйста, повторите попытку
          позже или <Link to="/new/">попробуйте ещё раз сейчас</Link>.
        </Trans>,
      ],
    },
    noSnils: {
      title: <Trans>Не проверен СНИЛС</Trans>,
      text: [
        <Trans>
          Воспользоваться услугой могут только пользователи с проверенным СНИЛС.
          Укажите СНИЛС в Личном кабинете и дождитесь его проверки.
        </Trans>,
        <Trans>Проверка СНИЛС занимает не более 24 часов.</Trans>,
      ],
    },
    juridical: {
      title: <Trans>Услуга недоступна</Trans>,
      text: [
        <Trans>
          Воспользоваться услугой могут только заявители физические лица.
          Используйте учётную запись физического лица.
        </Trans>,
      ],
    },
    youngerThan14: {
      title: <Trans>Услуга недоступна</Trans>,
      text: (
        <Trans>
          Воспользоваться услугой могут только заявители старше 14 лет
        </Trans>
      ),
    },
  },
};

export function ResultPage(props: Props): JSX.Element {
  const {
    location: { state: { status = null, statusName = '' } = {} },
    serviceNumber,
  } = props;

  useEffect(() => {
    const { resetLocationState } = props;
    window.scrollTo(0, 0);
    window.addEventListener('beforeunload', resetLocationState);
    return () => {
      window.removeEventListener('beforeunload', resetLocationState);
    };
  }, []);

  useEffect(() => {
    if ((status as string) === 'error') {
      switch (statusName) {
        case 'noSnils':
          sendStats(landingPageError('snils_not_verified'));
          break;
        case 'juridical':
          sendStats(landingPageError('account_corporate'));
          break;
        case 'youngerThan14':
          sendStats(landingPageError('account_younger_14'));
          break;
        case 'serviceNotAvailable':
          sendStats(landingPageError('service_unavailable'));
          break;
      }
    }
  }, [status]);

  if (status === null) {
    return <Redirect to="/" />;
  }

  const { title, text } = statusName
    ? statusTexts[status][statusName]
    : statusTexts[status];

  const showElkButton = ['noSnils', 'youngerThan14'].includes(statusName);

  const classNames = cn('ResultPage', {
    ResultPage_error: status === 'error',
  });
  return (
    <Fragment>
      {status === 'error' && (
        <div className="FormPage">
          <div className="FormPage__top">
            <Heading level={1} className="FormPage__title">
              <Trans>
                Присвоение (продление) льготного статуса многодетной семьи
                города Москвы
              </Trans>
            </Heading>
            <div className="FormPage__text">
              <Trans>
                Подать заявление может один из родителей (единственный
                родитель), отчим, мачеха многодетной семьи из числа граждан РФ,
                иностранных граждан, лиц без гражданства, имеющих место
                жительства в городе Москве, при условии его фактического
                совместного проживания с детьми, имеющими место жительства в
                городе Москве.
              </Trans>
            </div>
          </div>
        </div>
      )}
      <SuccessError className={classNames}>
        <Logo className="PrintLogo no-screen" />
        <SuccessError.Title
          className="no-screen ResultPage__title"
          type={status}
        >
          <Trans>
            Присвоение (продление) льготного статуса многодетной семьи города
            Москвы
          </Trans>
        </SuccessError.Title>
        <SuccessError.Title className="ResultPage__subTitle" type={status}>
          {title}
        </SuccessError.Title>
        <SuccessError.Content>
          {status === 'success' && (
            <Fragment>
              <Paragraph>
                <Trans>Номер вашей заявки {serviceNumber}</Trans>{' '}
                <Print
                  onClick={() => window.print()}
                  style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                />
              </Paragraph>
            </Fragment>
          )}
          {status !== 'success' &&
            [].concat(text).map((textPart, key) => (
              <Paragraph className="ResultPage_text" key={key}>
                {textPart}
              </Paragraph>
            ))}
          {showElkButton && (
            <a
              href={i18n._(t`https://my.mos.ru/my/`)}
              className="mos-button mos-button_type_primary ResultPage__action-button"
            >
              <Trans>Перейти в Личный кабинет</Trans>
            </a>
          )}
          <Success status={status} eno={serviceNumber} />
        </SuccessError.Content>

        <SuccessError.Actions />
      </SuccessError>
    </Fragment>
  );
}

const mapDispatchToProps = (dispatch) => ({
  resetLocationState: () =>
    dispatch(
      routerRedux.replace({
        state: { status: null, statusName: null },
      })
    ),
});

export default connect(
  ({ form }) => ({ serviceNumber: form.serviceNumber }),
  mapDispatchToProps
)(ResultPage);
